// Smoke pellet (kemuri-dama) art, all drawn in code from the palette (docs/STYLE.md): the pellet itself for the HUD, shop and
// inventory, and the geometry of the drifting cloud. Only palette colors are used; the cloud is bounded (PUFFS per cloud).
import { CONFIG } from '../../common/src/data';
import type { SmokeSnap } from '../../common/src/protocol';
import { COLOR, PALETTE } from './theme';

/** Puffs drawn per cloud (half that with "Low effects"); clouds are capped by config.smoke.maxClouds, so the total is bounded. */
export const PUFFS = 16;
const GROW_SEC = 0.5, FADE_SEC = 1.6;

/** A small round pellet with a lit fuse on a 2D canvas (HUD). `r` is the body radius in canvas pixels. */
export function drawPellet(g: CanvasRenderingContext2D, x: number, y: number, r: number) {
  g.fillStyle = COLOR.ground; g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
  g.strokeStyle = PALETTE.offWhite; g.lineWidth = Math.max(1, r * 0.22); g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.stroke();
  g.strokeStyle = PALETTE.slate; g.lineWidth = Math.max(1, r * 0.3); g.beginPath(); g.moveTo(x - r * 0.8, y + r * 0.1); g.lineTo(x + r * 0.8, y + r * 0.1); g.stroke();
  g.strokeStyle = PALETTE.offWhite; g.lineWidth = Math.max(1, r * 0.2); g.beginPath(); g.moveTo(x + r * 0.5, y - r * 0.8); g.lineTo(x + r * 0.9, y - r * 1.3); g.stroke();
  g.fillStyle = PALETTE.amber; g.beginPath(); g.arc(x + r * 0.95, y - r * 1.4, Math.max(1, r * 0.28), 0, Math.PI * 2); g.fill();
}

/** The same pellet as inline SVG for the DOM menus (shop card, inventory strip). */
export function pelletSvg(px = 20): string {
  return `<svg class="pellet" width="${px}" height="${px}" viewBox="0 0 20 20" aria-hidden="true"><circle cx="9" cy="12" r="6.5" fill="${COLOR.ground}" stroke="${PALETTE.offWhite}" stroke-width="1.4"/>`
    + `<path d="M3.4 12.6H14.6" stroke="${PALETTE.slate}" stroke-width="2"/><path d="M12 7.2L15 3.6" stroke="${PALETTE.offWhite}" stroke-width="1.2"/><circle cx="15.4" cy="3.2" r="1.6" fill="${PALETTE.amber}"/></svg>`;
}

/** Cheap deterministic hash in [0, 1) so every client draws the same puffs for the same cloud. */
const h01 = (a: number, b: number) => { const s = Math.sin(a * 127.1 + b * 311.7) * 43758.5453; return s - Math.floor(s); };

export interface Puff { x: number; y: number; r: number; a: number; light: boolean }
/**
 * The puffs of one cloud at animation time `t` (seconds, any monotonic clock): the cloud swells in over GROW_SEC, each puff drifts
 * on a slow loop and the whole cloud thins out over its last FADE_SEC. Pure, so a test can check bounds.
 */
export function cloudPuffs(c: SmokeSnap, t: number, count = PUFFS, duration = CONFIG.smoke.duration): Puff[] {
  const elapsed = Math.max(0, duration - c.t);
  const ease = 1 - (1 - Math.min(1, elapsed / GROW_SEC)) ** 3, fade = Math.min(1, Math.max(0, c.t) / FADE_SEC);
  const out: Puff[] = [];
  for (let i = 0; i < count; i++) {
    const ang = h01(c.id, i) * Math.PI * 2, rad = Math.sqrt(h01(c.id, i + 50)) * 0.78;
    const spin = (h01(c.id, i + 99) - 0.5) * 0.5, ph = h01(c.id, i + 150) * Math.PI * 2;
    const a = ang + t * spin;
    const wob = 0.06 * Math.sin(t * 0.9 + ph);
    const pr = c.r * (0.32 + 0.24 * h01(c.id, i + 200)) * (0.9 + 0.1 * Math.sin(t * 1.3 + ph));
    out.push({
      x: c.x + Math.cos(a) * (rad + wob) * c.r * ease + Math.sin(t * 0.4 + ph) * 2,
      y: c.y + Math.sin(a) * (rad + wob) * c.r * ease * 0.9 - elapsed * 1.2 * (0.5 + h01(c.id, i + 250)), // rises a little as it ages
      r: pr * ease, a: 0.26 * fade, light: h01(c.id, i + 300) > 0.45,
    });
  }
  return out;
}
export const PUFF_COLOR = { light: PALETTE.offWhite, dark: PALETTE.slate } as const;
